import * as THREE from 'three';
import { detailShading, turfGrainGlsl } from '@/rendering/DetailShading';
import { groundAnisotropy } from '@/rendering/TextureCapabilities';
import type { GroundCoverState } from '@/environment/EnvironmentState';
import {
  BASE_SURFACE_LAYER,
  SWATCH_TEXELS,
  paintedSurfaceLayers,
  type SurfaceLayer,
  type SurfaceMap,
} from './TerrainSurface';
import {
  WINTER_TEXELS,
  iceSwatch,
  snowSwatch,
  winterSwatchPixels,
  type WinterSwatch,
} from './WinterGround';

export interface TerrainMaterialOptions {
  /** Base ground color. */
  color?: THREE.ColorRepresentation;
}

/**
 * Stylized Stage-1 terrain material (spec §18): clean, matte, soft-shadow-friendly. A subtle
 * vertex-color gradient by slope is applied by {@link applySlopeTint} for readable relief without
 * textures, and {@link SurfaceTextures} layers the painted ground materials over that.
 *
 * The base color is white and the whole palette lives in the vertex colors. It used to be the grass
 * green, which multiplied against a grass-green vertex tint — squaring the color and darkening the
 * lawn. That was invisible while grass was the only ground there was; it stops being invisible the
 * moment a painted layer sits next to it at its own literal color, so the palette now lives in
 * exactly one place.
 */
export function createTerrainMaterial(options: TerrainMaterialOptions = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: options.color ?? 0xffffff,
    roughness: BASE_SURFACE_LAYER.roughness,
    metalness: 0,
    flatShading: false,
    vertexColors: true,
  });
}

const GRASS_TINT = new THREE.Color(BASE_SURFACE_LAYER.color);
const SOIL_TINT = new THREE.Color(0x8a6f4a);

/**
 * Compute per-vertex colors from surface steepness: flatter areas read as grass green, steeper
 * faces as exposed earth. Writes a `color` attribute onto the geometry (which already has normals).
 */
export function applySlopeTint(
  geometry: THREE.BufferGeometry,
  grass: THREE.Color = GRASS_TINT,
  soil: THREE.Color = SOIL_TINT,
): void {
  const normal = geometry.getAttribute('normal');
  if (!normal) return;
  const count = normal.count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    writeSlopeTint(colors, i, normal.getY(i), grass, soil);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

/**
 * Write one vertex's slope tint. Split out of {@link applySlopeTint} so a sculpt edit can re-tint
 * just the vertices it moved with the identical formula, rather than a second implementation that
 * drifts.
 */
export function writeSlopeTint(
  colors: Float32Array,
  index: number,
  normalY: number,
  grass: THREE.Color = GRASS_TINT,
  soil: THREE.Color = SOIL_TINT,
): void {
  // Steepness: 0 when flat (normal.y = 1), 1 when vertical.
  const steepness = THREE.MathUtils.clamp(1 - normalY, 0, 1);
  const t = THREE.MathUtils.smoothstep(steepness, 0.25, 0.6);
  colors[index * 3] = grass.r + (soil.r - grass.r) * t;
  colors[index * 3 + 1] = grass.g + (soil.g - grass.g) * t;
  colors[index * 3 + 2] = grass.b + (soil.b - grass.b) * t;
}

/* ------------------------------ Painted ground ------------------------------ */

/**
 * The GPU-side view of a {@link SurfaceMap}: an RGBA byte **array** texture holding the layer
 * weights, one array layer per splat page.
 *
 * Deliberately **not** color data — these are weights, so the texture carries no color space and is
 * never sRGB-decoded on sampling. Linear filtering is on, which is what feathers a painted edge
 * across the gap between texels and is why the paint can look smooth at 12 cm resolution.
 *
 * An array texture rather than N separate textures because the map's bytes are already page-major:
 * the upload is the map's own array with no repacking, and one `needsUpdate` re-sends every page.
 */
export function createSplatTexture(map: SurfaceMap): THREE.DataArrayTexture {
  const texture = new THREE.DataArrayTexture(map.data, map.width, map.height, map.pages);
  texture.name = 'terrain-splat';
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

/**
 * The tiled albedo swatches, one array layer per splat slot.
 *
 * Every swatch is normalised to {@link SWATCH_TEXELS} before it reaches here precisely so that one
 * array texture can hold all of them — the alternative is a uniform sampler per layer, which caps
 * the palette at whatever the driver's texture-unit budget happens to be and costs a shader
 * recompile every time a material is added.
 *
 * Mipmapped and anisotropically filtered, because tiled ground is the worst case for aliasing: a
 * gravel swatch at 1.5 m per tile viewed down a driveway is a minification of forty or more, and
 * without mips it boils.
 */
function createAlbedoTexture(depth: number): THREE.DataArrayTexture {
  const size = SWATCH_TEXELS * SWATCH_TEXELS * 4;
  const data = new Uint8Array(size * depth);
  // White, opaque: a slot with no swatch is never sampled, but an uninitialised black one would be
  // a very visible bug the first time the guard was wrong.
  data.fill(255);
  const texture = new THREE.DataArrayTexture(data, SWATCH_TEXELS, SWATCH_TEXELS, depth);
  texture.name = 'terrain-albedo';
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // Sampled with an explicit gradient and decoded in the shader, so the texture itself stays raw.
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = true;
  // Whatever the driver will give, up to a cap — see `TextureCapabilities`. Was a flat 8, which
  // under-used every discrete GPU and over-asked the handful that cannot manage it.
  texture.anisotropy = groundAnisotropy();
  texture.needsUpdate = true;
  return texture;
}

/** A 1×1×1 stand-in, for the common case where no layer has a swatch. Costs one texel of VRAM. */
function createEmptyAlbedoTexture(): THREE.DataArrayTexture {
  const texture = new THREE.DataArrayTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, 1);
  texture.name = 'terrain-albedo-empty';
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

/**
 * A winter cover swatch on the GPU: one plain 2D texture, tiled over the whole site.
 *
 * Not an array layer like the palette's swatches, because the cover is not a palette entry — there is
 * exactly one snow and one ice in force at a time, they are chosen rather than painted, and giving
 * them splat slots would spend two of the sixteen materials a garden is allowed on something no
 * texel ever refers to.
 *
 * Mipmapped and anisotropic for the same reason the albedo array is: a 3 m tile seen down the length
 * of a garden is a minification of thirty or more, and unfiltered snow boils.
 */
function createWinterTexture(pixels: Uint8Array<ArrayBuffer>): THREE.DataTexture {
  const texture = new THREE.DataTexture(pixels, WINTER_TEXELS, WINTER_TEXELS);
  texture.name = 'terrain-winter';
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // Sampled through an explicit gradient and decoded in the shader, like the albedo array.
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = true;
  texture.anisotropy = groundAnisotropy();
  texture.needsUpdate = true;
  return texture;
}

/** A 1×1 stand-in for a cover that is switched off. The sampler must still be bound. */
function createEmptyWinterTexture(): THREE.DataTexture {
  const texture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
  texture.name = 'terrain-winter-empty';
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

/** Decoded swatch pixels, keyed by data URL. Decoding is async; the same image is decoded once. */
const swatchPixelCache = new Map<string, Uint8Array>();

/**
 * Decode a swatch data URL to `SWATCH_TEXELS²` RGBA bytes.
 *
 * Resamples rather than trusting the stored size: a swatch can reach the palette from an upload the
 * user picked, and refusing a 512-pixel PNG because it is not 256 would be a poor way to meet
 * someone's texture. Returns null wherever there is no canvas — Node under test, mainly — so the
 * palette still works headless and simply renders flat colours.
 */
export async function decodeSwatchPixels(dataUrl: string): Promise<Uint8Array | null> {
  const cached = swatchPixelCache.get(dataUrl);
  if (cached) return cached;
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;

  const image = await loadImage(dataUrl);
  if (!image) return null;

  const canvas = document.createElement('canvas');
  canvas.width = SWATCH_TEXELS;
  canvas.height = SWATCH_TEXELS;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  // Matters most for projects saved before the swatch size went up: those hold 256-pixel images and
  // are being drawn to 512 here, where the default nearest-ish upscale would show as blocking.
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, 0, 0, SWATCH_TEXELS, SWATCH_TEXELS);
  const pixels = new Uint8Array(
    context.getImageData(0, 0, SWATCH_TEXELS, SWATCH_TEXELS).data.buffer.slice(0),
  );
  swatchPixelCache.set(dataUrl, pixels);
  return pixels;
}

function loadImage(dataUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

/**
 * Everything the ground shader is bound to, kept in one place so it can be *re-*bound.
 *
 * The palette changes at runtime now — a forged texture, an uploaded swatch, a retired layer — and
 * each of those has to reach the GPU. Splitting this out of a one-shot `attach` call is what makes
 * that possible without rebuilding the mesh: the uniform *objects* are held here and mutated in
 * place, so a colour or a tiling change is free, and only a change in the number of splat pages
 * costs a shader recompile (the loop bound is a compile-time constant).
 */
export class SurfaceTextures {
  private splat: THREE.DataArrayTexture;
  private albedo: THREE.DataArrayTexture;
  private albedoDepth = 0;
  private snow: THREE.DataTexture;
  private ice: THREE.DataTexture;
  /** Which variant each cover texture currently holds, so re-picking the same one uploads nothing. */
  private snowVariant: string | null = null;
  private iceVariant: string | null = null;

  private readonly colors: THREE.Color[] = [];
  private readonly roughness: number[] = [];
  /** Tiles per metre per slot. Zero means "no swatch yet — use the flat colour". */
  private readonly tiles: number[] = [];
  private readonly siteMeters = new THREE.Vector2(1, 1);

  /**
   * The uniform objects themselves, owned here rather than created inside `onBeforeCompile`.
   *
   * This is load-bearing and was a real bug before it was. Three caches compiled programs by
   * `customProgramCacheKey`, and a material that recompiles under a key it has used before gets the
   * cached program **and its existing uniforms** — `onBeforeCompile` does not run again. So swapping
   * a texture by reassigning `shader.uniforms.x = { value: next }` from a second `attach()` call
   * silently did nothing, and a forged material rendered as the blank white placeholder its array
   * texture had been initialised with. Holding the uniform objects and mutating `.value` means the
   * binding survives whether the program was recompiled or reused.
   */
  private readonly uniforms: {
    uSurfaceSplat: { value: THREE.DataArrayTexture };
    uSurfaceAlbedo: { value: THREE.DataArrayTexture };
    uSurfaceColor: { value: THREE.Color[] };
    uSurfaceRoughness: { value: number[] };
    uSurfaceTiles: { value: number[] };
    uSurfaceSiteMeters: { value: THREE.Vector2 };
    uWinterSnow: { value: THREE.DataTexture };
    uWinterIce: { value: THREE.DataTexture };
    /** How much of the ground each cover has taken, snow in x and ice in y. Zero is "not there". */
    uWinterAmount: { value: THREE.Vector2 };
    /** Tiles per metre: snow, ice, and the coarse drift mask that patches one over the other. */
    uWinterTiles: { value: THREE.Vector3 };
    /** Roughness under each cover, snow in x and ice in y. */
    uWinterRoughness: { value: THREE.Vector2 };
  };

  private material: THREE.MeshStandardMaterial | null = null;
  private compiledPages = 0;
  /** Which data URL is currently uploaded into each array layer, so re-syncs are cheap. */
  private readonly uploaded = new Map<number, string>();

  constructor(private map: SurfaceMap) {
    this.splat = createSplatTexture(map);
    this.albedo = createEmptyAlbedoTexture();
    this.snow = createEmptyWinterTexture();
    this.ice = createEmptyWinterTexture();
    this.siteMeters.set(map.widthMeters, map.lengthMeters);
    this.readPalette();
    this.uniforms = {
      uSurfaceSplat: { value: this.splat },
      uSurfaceAlbedo: { value: this.albedo },
      uSurfaceColor: { value: this.colors },
      uSurfaceRoughness: { value: this.roughness },
      uSurfaceTiles: { value: this.tiles },
      uSurfaceSiteMeters: { value: this.siteMeters },
      uWinterSnow: { value: this.snow },
      uWinterIce: { value: this.ice },
      uWinterAmount: { value: new THREE.Vector2(0, 0) },
      uWinterTiles: { value: new THREE.Vector3(0, 0, 0) },
      uWinterRoughness: { value: new THREE.Vector2(0.9, 0.1) },
    };
    // After the uniforms exist, because the upload re-points `uSurfaceAlbedo` at a sized array.
    //
    // The `readPalette` above can only ever set every tiling to zero: it gates the sampler on pixels
    // actually being on the GPU, and at construction none are. Without this, a texture set built
    // against a palette that is *already* loaded never uploads anything — and that is the normal
    // case for opening a project, because `Application.applyProject` loads the palette before it
    // builds the terrain, so the terrain is born matching `surfacePalette.revision` and
    // `Terrain.syncPalette` correctly declines to do anything. The symptom was a site whose forged
    // materials all rendered as their flat average colour until something else touched the palette.
    void this.syncAlbedo();
  }

  /** Slots the shader compiles uniform arrays for. */
  private get slots(): number {
    return this.map.pages * 4;
  }

  /**
   * Bind to a material.
   *
   * Injected into the stock physical shader rather than written from scratch, so the terrain keeps
   * every shadow, fog, tone-mapping and light-probe path the rest of the scene uses — a bespoke
   * ShaderMaterial would have to re-earn all of that and would fall behind the first time the
   * lighting changed.
   *
   * Both color *and* roughness are blended. Roughness is what actually sells the difference: mulch,
   * asphalt and water can be told apart at a glance in a still frame only because the sun catches
   * them differently, and a color-only splat leaves a pond looking like blue felt.
   */
  attach(material: THREE.MeshStandardMaterial): void {
    this.material = material;
    this.compiledPages = this.map.pages;

    const pages = this.compiledPages;
    const slots = pages * 4;
    material.defines = { ...material.defines, USE_UV: '' };
    material.onBeforeCompile = (shader) => {
      // Assigned by reference — see the note on `uniforms`. The detail switch is shared with every
      // flora material in the scene, so the ground and the planting turn over together.
      Object.assign(shader.uniforms, this.uniforms, detailShading.uniforms);

      const turf = turfGrainGlsl();
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\n${commonChunk(pages, slots)}\n${turf.common}`)
        .replace('#include <color_fragment>', `#include <color_fragment>\n${colorChunk(turf.albedo)}`)
        .replace(
          '#include <roughnessmap_fragment>',
          `#include <roughnessmap_fragment>
roughnessFactor = roughnessFactor * lgBase + lgRough;`,
        )
        // The pile of the lawn, as relief. Ground is the one surface in the scene always seen at a
        // grazing angle, so this is the injection most in need of the distance fade that comes with
        // it — see `DetailShading`.
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\n${turf.bump}`);
    };
    // Programs are cached by source; without a distinct key a painted terrain could reuse the plain
    // one's compiled program and silently lose the injection. The page count is part of the key
    // because it is baked into the loop bound.
    material.customProgramCacheKey = () => `lg-terrain-surface-${pages}`;
    material.needsUpdate = true;
  }

  /**
   * Re-read the palette and the map, uploading whatever changed.
   *
   * Returns whether the material had to be recompiled, which only happens when the palette crossed
   * into another splat page. The caller does not need the answer — it is reported so the one
   * genuinely expensive path is visible in a profile rather than hidden inside a routine sync.
   */
  sync(map: SurfaceMap): boolean {
    const pagesChanged = map.pages !== this.compiledPages;
    const storageChanged =
      map.data !== this.splat.image.data ||
      map.pages !== this.splat.image.depth ||
      map.width !== this.splat.image.width ||
      map.height !== this.splat.image.height;
    this.map = map;
    this.siteMeters.set(map.widthMeters, map.lengthMeters);

    if (storageChanged) {
      // Replaced rather than re-pointed. Three allocates an array texture's GPU storage once, at
      // its first upload, and thereafter every `needsUpdate` is a `texSubImage3D` into that fixed
      // allocation — so a texture that was allocated one page deep and then handed three pages of
      // data writes off the end of it. That failed as a GL warning and a garbled ground rather
      // than as an exception, which is exactly the kind of thing to make impossible by construction.
      this.splat.dispose();
      this.splat = createSplatTexture(map);
      this.uniforms.uSurfaceSplat.value = this.splat;
    }
    this.splat.needsUpdate = true;

    this.readPalette();
    void this.syncAlbedo();

    if (pagesChanged && this.material) {
      this.attach(this.material);
      return true;
    }
    return false;
  }

  /** Push a paint edit to the GPU. The whole splat is re-sent; it is one small texture. */
  touch(): void {
    this.splat.needsUpdate = true;
  }

  /**
   * Lay snow and/or ice over the whole ground, or take them off again.
   *
   * Nothing about the paint map is touched — this is a uniform change and, the first time a variant
   * is asked for, one texture upload. That is the point of doing it in the shader: switching winter
   * on and off is free and lossless, where painting a snow layer over the site would be neither.
   *
   * Drawing a swatch is a few tens of milliseconds of arithmetic on the main thread, paid once per
   * variant per session (`winterSwatchPixels` caches the bytes), and only when a cover is actually
   * asked for. A site that never sees winter never draws a flake.
   */
  setWinterCover(cover: GroundCoverState | null): void {
    const snow = cover?.snow ? snowSwatch(cover.snowSwatch) : null;
    const ice = cover?.ice ? iceSwatch(cover.iceSwatch) : null;

    if (snow) this.snow = this.adoptCover(this.snow, snow, 'snowVariant');
    if (ice) this.ice = this.adoptCover(this.ice, ice, 'iceVariant');
    this.uniforms.uWinterSnow.value = this.snow;
    this.uniforms.uWinterIce.value = this.ice;

    this.uniforms.uWinterAmount.value.set(
      snow ? (cover?.snowAmount ?? 1) : 0,
      ice ? (cover?.iceAmount ?? 1) : 0,
    );
    this.uniforms.uWinterTiles.value.set(
      snow ? 1 / snow.tileMeters : 0,
      ice ? 1 / ice.tileMeters : 0,
      snow ? 1 / snow.driftMeters : 0,
    );
    this.uniforms.uWinterRoughness.value.set(snow?.roughness ?? 0.9, ice?.roughness ?? 0.1);
  }

  /**
   * Bring one cover texture to `swatch`, reusing it when the variant has not changed.
   *
   * The old texture is disposed rather than re-pointed for the reason given on `sync`: three sizes a
   * texture's GPU storage at its first upload, and the placeholder every cover starts as is 1×1.
   */
  private adoptCover(
    current: THREE.DataTexture,
    swatch: WinterSwatch,
    slot: 'snowVariant' | 'iceVariant',
  ): THREE.DataTexture {
    if (this[slot] === swatch.id) return current;
    current.dispose();
    this[slot] = swatch.id;
    return createWinterTexture(winterSwatchPixels(swatch));
  }

  private readPalette(): void {
    const layers = paintedSurfaceLayers();
    const slots = this.slots;
    this.colors.length = 0;
    this.roughness.length = 0;
    this.tiles.length = 0;
    for (let slot = 0; slot < slots; slot += 1) {
      const layer = layers.find((entry) => entry.channel === slot);
      this.colors.push(new THREE.Color(layer?.color ?? '#ffffff'));
      this.roughness.push(layer?.roughness ?? 1);
      // Tiling is the sampler's gate, and it stays shut until this slot's pixels are actually on
      // the GPU. Otherwise the ground samples the array's blank fill for the frames between a
      // material being added and its swatch finishing decoding, and a new bed flashes white.
      const uploaded = layer !== undefined && this.uploaded.get(layer.channel) === layer.swatch?.dataUrl;
      this.tiles.push(uploaded ? swatchTiles(layer) : 0);
    }
  }

  /**
   * Bring the albedo array in step with the palette.
   *
   * Async because decoding a PNG is, and deliberately fire-and-forget: the ground renders with the
   * layer's flat average colour until the swatch lands, which is a frame or two after a forge
   * completes and is a far better behaviour than blocking the paint stroke that triggered it. The
   * tiling uniform is what gates the sampler, and it is only raised above zero once the pixels are
   * actually uploaded.
   */
  private async syncAlbedo(): Promise<void> {
    const layers = paintedSurfaceLayers().filter((layer) => layer.swatch);
    if (layers.length === 0) {
      this.uploaded.clear();
      return;
    }

    const depth = this.slots;
    if (this.albedoDepth !== depth) {
      this.albedo.dispose();
      this.albedo = createAlbedoTexture(depth);
      this.albedoDepth = depth;
      this.uploaded.clear();
      // Re-point the uniform we own. Re-running `attach` here would not work: the cache key has not
      // changed, so three would hand back the cached program without re-running `onBeforeCompile`.
      this.uniforms.uSurfaceAlbedo.value = this.albedo;
    }

    const image = this.albedo.image as { data: Uint8Array };
    const stride = SWATCH_TEXELS * SWATCH_TEXELS * 4;
    let changed = false;

    for (const layer of layers) {
      const swatch = layer.swatch;
      if (!swatch || layer.channel < 0 || layer.channel >= depth) continue;
      if (this.uploaded.get(layer.channel) === swatch.dataUrl) continue;
      const pixels = await decodeSwatchPixels(swatch.dataUrl);
      if (!pixels || pixels.length !== stride) continue;
      image.data.set(pixels, layer.channel * stride);
      this.uploaded.set(layer.channel, swatch.dataUrl);
      changed = true;
    }

    if (changed) {
      this.albedo.needsUpdate = true;
      // Only now is the sampler safe to read from, so this is where tiling is switched on.
      this.readPalette();
    }
  }

  dispose(): void {
    this.splat.dispose();
    this.albedo.dispose();
    this.snow.dispose();
    this.ice.dispose();
    this.uploaded.clear();
    this.material = null;
  }
}

/** Tiles per metre for a layer's swatch, or 0 when it has none. */
function swatchTiles(layer: SurfaceLayer | undefined): number {
  const tileMeters = layer?.swatch?.tileMeters;
  if (tileMeters === undefined || !(tileMeters > 0)) return 0;
  return 1 / tileMeters;
}

/**
 * Uniform declarations and the sRGB decode.
 *
 * The decode is written out rather than borrowed from three's `sRGBTransferEOTF`: the swatch array
 * is sampled through a hand-written `textureGrad` that three knows nothing about, so nothing else
 * will linearise it, and depending on a chunk-internal helper name is exactly the kind of coupling
 * that breaks silently on an engine upgrade. The flat layer colours need no decode — `THREE.Color`
 * already converts them into working space on the way in.
 */
function commonChunk(pages: number, slots: number): string {
  return `
#define LG_SPLAT_PAGES ${pages}
uniform sampler2DArray uSurfaceSplat;
uniform sampler2DArray uSurfaceAlbedo;
uniform vec3 uSurfaceColor[ ${slots} ];
uniform float uSurfaceRoughness[ ${slots} ];
uniform float uSurfaceTiles[ ${slots} ];
uniform vec2 uSurfaceSiteMeters;
uniform sampler2D uWinterSnow;
uniform sampler2D uWinterIce;
uniform vec2 uWinterAmount;
uniform vec3 uWinterTiles;
uniform vec2 uWinterRoughness;
float lgBase;
float lgRough;
vec3 lgPaint;
vec3 lgToLinear( vec3 c ) {
  return mix(
    pow( ( c + 0.055 ) / 1.055, vec3( 2.4 ) ),
    c / 12.92,
    vec3( lessThanEqual( c, vec3( 0.04045 ) ) )
  );
}`;
}

/**
 * Blend the painted layers over the slope tint.
 *
 * Weights below a quarter of a byte are skipped rather than blended. Two reasons, and the second is
 * the load-bearing one: a texel carrying 1/255 of a layer contributes nothing anyone can see, and
 * the skip is what keeps the cost proportional to the materials actually *present* at a fragment
 * rather than to the size of the palette. A sixteen-material site still samples one or two swatches
 * per pixel, because that is how many materials any one patch of ground is made of.
 *
 * Gradients are taken once, in uniform control flow, and handed to `textureGrad`. Sampling with
 * `texture()` inside the branch would leave the derivative undefined at exactly the edges where two
 * materials meet, which is where the seam would show.
 *
 * `turfAlbedo` is handed in rather than written here because the lawn's grain is one member of a
 * family — the same noise field, at other frequencies, gives a canopy its clumping and a trunk its
 * fissures — and the family has to live in one file if the ground and the planting are ever to look
 * like they were lit by the same afternoon. See `DetailShading`.
 */
function colorChunk(turfAlbedo: string): string {
  return `
{
  vec2 lgGround = vUv * uSurfaceSiteMeters;
  vec2 lgDdx = dFdx( lgGround );
  vec2 lgDdy = dFdy( lgGround );
  lgBase = 1.0;
  lgRough = 0.0;
  lgPaint = vec3( 0.0 );
  for ( int lgPage = 0; lgPage < LG_SPLAT_PAGES; lgPage ++ ) {
    vec4 lgWeights = texture( uSurfaceSplat, vec3( vUv, float( lgPage ) ) );
    for ( int lgC = 0; lgC < 4; lgC ++ ) {
      float lgW = lgWeights[ lgC ];
      if ( lgW < 0.004 ) continue;
      int lgSlot = lgPage * 4 + lgC;
      float lgTiles = uSurfaceTiles[ lgSlot ];
      vec3 lgAlbedo = uSurfaceColor[ lgSlot ];
      if ( lgTiles > 0.0 ) {
        lgAlbedo = lgToLinear( textureGrad(
          uSurfaceAlbedo,
          vec3( lgGround * lgTiles, float( lgSlot ) ),
          lgDdx * lgTiles,
          lgDdy * lgTiles
        ).rgb );
      }
      lgPaint += lgAlbedo * lgW;
      lgRough += uSurfaceRoughness[ lgSlot ] * lgW;
      lgBase -= lgW;
    }
  }
  lgBase = clamp( lgBase, 0.0, 1.0 );
  diffuseColor.rgb = diffuseColor.rgb * lgBase + lgPaint;
${winterChunk()}
  // Last, and deliberately so. lgBase is the share of this fragment that is still bare ground, and
  // winter has just written it down wherever snow or ice lies — so weighting the grain by it here,
  // rather than before the cover went on, is what keeps a lawn's pile from showing through the snow
  // on top of it. One expression covers both "this is a gravel bed" and "this is under 30 cm of snow".
${turfAlbedo}
  // A pile that is not perfectly even also does not scatter perfectly evenly. Small, and carried by
  // the same fades as the tone, so it dies out with the rest of the grain rather than leaving the
  // lawn subtly glossier at fifty metres than it is at five.
  lgRough += ( lgHeight - 0.5 ) * 0.1 * uLgDetail * lgFarFade;
}`;
}

/**
 * Lay the winter cover over the finished ground.
 *
 * After the paint rather than as part of it, and that ordering is the whole design: the cover has no
 * channel, no weights and no place in the palette — it is a thing that happens to the result. Turning
 * it off restores the garden exactly, because the garden was never edited.
 *
 * Both branches are gated on uniforms alone, so the control flow is uniform across the draw and the
 * implicit derivative in the drift-mask sample is well defined. A site with no cover pays two
 * comparisons per ground fragment and takes no samples.
 *
 * **Ice is a window, not a coat.** It tints and darkens what it froze over and only wins outright
 * where the swatch says the sheet is opaque — along the fractures, in the trapped air, in rime. That
 * is both what ice looks like and what keeps the drawing readable: the beds and the paving are still
 * legible through it, which they would not be under a flat grey glaze.
 *
 * **Snow retreats to where it lies deepest.** The cover's own fourth channel, sampled at the much
 * coarser drift scale, is a depth map, so thinning the fall breaks it up into patches metres across
 * instead of fading it uniformly — which is what a thin fall does, and what makes the ice underneath
 * show through in the one case where both covers are on.
 */
function winterChunk(): string {
  return `
  if ( uWinterAmount.y > 0.0 ) {
    float lgIceT = uWinterTiles.y;
    vec4 lgIceTex = textureGrad( uWinterIce, lgGround * lgIceT, lgDdx * lgIceT, lgDdy * lgIceT );
    // Transmission through a few millimetres of ice: darker, and pushed blue-green.
    vec3 lgSeen = diffuseColor.rgb * vec3( 0.44, 0.52, 0.58 );
    vec3 lgGlaze = mix( lgSeen, lgToLinear( lgIceTex.rgb ), lgIceTex.a );
    diffuseColor.rgb = mix( diffuseColor.rgb, lgGlaze, uWinterAmount.y );
    lgBase = mix( lgBase, 0.0, uWinterAmount.y );
    lgRough = mix( lgRough, uWinterRoughness.y, uWinterAmount.y );
  }
  if ( uWinterAmount.x > 0.0 ) {
    float lgSnowT = uWinterTiles.x;
    vec4 lgSnowTex = textureGrad( uWinterSnow, lgGround * lgSnowT, lgDdx * lgSnowT, lgDdy * lgSnowT );
    // Held back a little when there is ice underneath, so a full fall still leaves the wind-scoured
    // patches that are the only way to see there is ice under it at all.
    float lgFall = uWinterAmount.x * ( uWinterAmount.y > 0.0 ? 0.78 : 1.0 );
    float lgDrift = texture( uWinterSnow, lgGround * uWinterTiles.z ).a;
    // A threshold on depth that slides with the fall: 1 everywhere at a full fall, nothing at zero.
    float lgEdge = 1.2 - 1.4 * lgFall;
    float lgLies = smoothstep( lgEdge - 0.18, lgEdge + 0.18, lgDrift );
    diffuseColor.rgb = mix( diffuseColor.rgb, lgToLinear( lgSnowTex.rgb ), lgLies );
    lgBase = mix( lgBase, 0.0, lgLies );
    lgRough = mix( lgRough, uWinterRoughness.x, lgLies );
  }`;
}
